import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setFamilyMembers, toggleFamilyMemberSelection, setRegisteredMemberIds } from '../redux/slices/bookingSlice';
import { selectFamilyMembers, deselectFamilyMember } from '../redux/slices/bookingSlice';
import StepperButtons from '../components/StepperButtons';
import RegistrationStepperHeader from '../components/RegistrationStepperHeader';
import eventService from '../services/eventService';
import Swal from 'sweetalert2';

const FamilySelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { selectedEvent } = useSelector((state) => state.event);
  const { selectedCenter } = useSelector((state) => state.event);
  const { eventDetails, devoteeInfo, familyMembers, selectedFamilyMemberIds, registeredMemberIds, totalPrice } = useSelector((state) => state.booking);
  const [loading, setLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  
  // Add/Edit student modal state
  const [showAddFamilyModal, setShowAddFamilyModal] = useState(false);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState(null);
  const [familyFormData, setFamilyFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    relationship: '',
    gotram: '',
    nastram: '',
  });
  const [familyFormErrors, setFamilyFormErrors] = useState({
    firstName: false,
    lastName: false,
    dateOfBirth: false,
    gender: false,
    relationship: false,
    gotram: false,
    nastram: false,
  });

  useEffect(() => {
    if (!devoteeInfo.email) {
      navigate('/devotee');
      return;
    }

    if (!isAuthenticated || !user) {
      navigate('/devotee');
      return;
    }

    const fetchFamilyMembers = async () => {
      // Prevent duplicate calls
      if (hasFetchedRef.current) {
        console.log('FamilySelection - Already fetched, skipping duplicate call');
        return;
      }

      try {
        hasFetchedRef.current = true;
        setLoading(true);
        const userId = user?.userId || user?.user_Id || user?.data?.userId || user?.data?.user_Id || user?.userData?.user_Id;
        const eventId = selectedEvent?.event_Id || selectedEvent?.eventId;

        console.log('FamilySelection - Fetching family members for userId:', userId, 'eventId:', eventId);

        if (!userId) {
          console.warn('FamilySelection - Missing userId');
          setLoading(false);
          hasFetchedRef.current = false;
          return;
        }

        // Fetch all students (called once)
        const studentsData = await eventService.getStudents(userId);
        console.log('FamilySelection - Students data received:', studentsData);
        const students = studentsData?.students || studentsData || [];

        const formattedMembers = students.map(student => ({
          id: student.studentId || student.student_Id || student.Student_Id,
          firstName: student.firstName || student.student_First_Name || student.Student_First_Name || '',
          lastName: student.lastName || student.student_Last_Name || student.Student_Last_Name || '',
          dateOfBirth: student.dateOfBirth || student.student_Date_Of_Birth || student.Student_Date_Of_Birth || '',
          gender: student.gender || student.student_Gender || student.Student_Gender || '',
          relationship: student.relationship || student.student_Relationship || student.Student_Relationship || '',
          gotram: student.gotram || student.student_Gothram || student.Student_Gothram || '',
          nastram: student.nastram || student.student_Nakshatram || student.Student_Nakshatram || '',
        }));

        // Filter out the devotee from family members (anyone with relationship='Self')
        // Also filter if it's the EXACT same person (same ID OR same name AND same DOB)
        const filteredMembers = formattedMembers.filter(member => {
          // First check: Filter out anyone with relationship 'Self' (the logged-in devotee)
          const relationship = (member.relationship || '').toLowerCase().trim();
          if (relationship === 'self') {
            console.log('FamilySelection - Filtering out devotee with relationship=Self:', member.firstName, member.lastName);
            return false;
          }
          
          // Check by ID if devotee has studentId
          const isSameById = devoteeInfo.studentId && member.id === devoteeInfo.studentId;
          
          if (isSameById) {
            console.log('FamilySelection - Filtering out devotee by ID:', member.firstName, member.lastName, 'ID:', member.id);
            return false;
          }
          
          // Check by name AND date of birth (both must match to be considered same person)
          const memberName = `${member.firstName} ${member.lastName}`.toLowerCase().trim();
          const devoteeName = `${devoteeInfo.firstName} ${devoteeInfo.lastName}`.toLowerCase().trim();
          const isSameName = memberName === devoteeName;
          
          if (isSameName) {
            // Same name - also check DOB to confirm it's the same person
            const memberDOB = member.dateOfBirth ? new Date(member.dateOfBirth).toISOString().split('T')[0] : '';
            const devoteeDOB = devoteeInfo.dateOfBirth ? new Date(devoteeInfo.dateOfBirth).toISOString().split('T')[0] : '';
            const isSameDOB = memberDOB && devoteeDOB && memberDOB === devoteeDOB;
            
            if (isSameDOB) {
              console.log('FamilySelection - Filtering out devotee by name AND DOB:', member.firstName, member.lastName);
              return false;
            } else {
              console.log('FamilySelection - Same name but different DOB, keeping in list:', member.firstName, member.lastName);
            }
          }
          
          return true;
        });

        // Merge with existing family members to preserve any added during this session
        // Only add members from API that don't already exist in Redux
        const existingIds = familyMembers.map(m => m.id);
        const newMembers = filteredMembers.filter(m => !existingIds.includes(m.id));
        
        if (newMembers.length > 0 || familyMembers.length === 0) {
          // If there are new members from API or Redux is empty, merge them
          const mergedMembers = [...familyMembers, ...newMembers];
          console.log('FamilySelection - Existing:', familyMembers.length, 'New from API:', newMembers.length, 'Total:', mergedMembers.length);
          dispatch(setFamilyMembers(mergedMembers));
        } else {
          console.log('FamilySelection - No new members from API, keeping existing:', familyMembers.length);
        }

        // Fetch registered students for this event (called once)
        if (eventId) {
          try {
            const registeredData = await eventService.getRegisteredStudents(userId, eventId);
            console.log('FamilySelection - Registered students data:', registeredData);
            const registered = registeredData?.students || registeredData || [];
            
            const registeredIds = registered.map(student => 
              student.studentId || student.student_Id || student.Student_Id
            );
            
            console.log('FamilySelection - Registered student IDs:', registeredIds);
            dispatch(setRegisteredMemberIds(registeredIds));
          } catch (regError) {
            console.warn('FamilySelection - Error fetching registered students:', regError);
            // Continue even if this fails
          }
        }
      } catch (error) {
        console.error('FamilySelection - Error fetching family members:', error);
        hasFetchedRef.current = false; // Reset on error to allow retry
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyMembers();
  }, [devoteeInfo.email, isAuthenticated, user, selectedEvent, dispatch, navigate]);

  const handleToggleMember = (memberId) => {
    // Prevent selection of already registered members
    if (registeredMemberIds.includes(memberId)) {
      console.log('Member already registered for this event, cannot select');
      return;
    }
    
    console.log('=== TOGGLE DEBUG ===');
    console.log('Clicked member ID:', memberId, 'Type:', typeof memberId);
    console.log('Current selectedFamilyMemberIds:', selectedFamilyMemberIds);
    console.log('Selected IDs types:', selectedFamilyMemberIds.map(id => typeof id));
    console.log('Is currently selected?', selectedFamilyMemberIds.includes(memberId));
    dispatch(toggleFamilyMemberSelection(memberId));
    console.log('===================');
  };

  const isMemberSelected = (memberId) => {
    return selectedFamilyMemberIds.includes(memberId);
  };

  // Add Student Handler
  const handleAddStudent = () => {
    setFamilyFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      relationship: '',
      gotram: '',
      nastram: '',
    });
    setFamilyFormErrors({
      firstName: false,
      lastName: false,
      dateOfBirth: false,
      gender: false,
      relationship: false,
      gotram: false,
      nastram: false,
    });
    setSelectedFamilyMember(null);
    setShowAddFamilyModal(true);
  };

  // Edit Student Handler
  const handleEditStudent = (e, member) => {
    e.stopPropagation();
    setSelectedFamilyMember(member);
    
    let dob = member.dateOfBirth || '';
    if (dob) {
      try {
        const date = new Date(dob);
        dob = date.toISOString().split('T')[0];
      } catch (err) {
        console.error('Error parsing date:', err);
      }
    }
    
    let gender = member.gender || '';
    if (gender === 'M' || gender === 'm') {
      gender = 'Male';
    } else if (gender === 'F' || gender === 'f') {
      gender = 'Female';
    }
    
    setFamilyFormData({
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      dateOfBirth: dob,
      gender: gender,
      relationship: member.relationship || '',
      gotram: member.gotram || '',
      nastram: member.nastram || '',
    });
    setFamilyFormErrors({
      firstName: false,
      lastName: false,
      dateOfBirth: false,
      gender: false,
      relationship: false,
      gotram: false,
      nastram: false,
    });
    setShowAddFamilyModal(true);
  };

  // Close Modal Handler
  const handleCloseFamilyModal = () => {
    setShowAddFamilyModal(false);
    setSelectedFamilyMember(null);
    setFamilyFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      relationship: '',
      gotram: '',
      nastram: '',
    });
    setFamilyFormErrors({
      firstName: false,
      lastName: false,
      dateOfBirth: false,
      gender: false,
      relationship: false,
      gotram: false,
      nastram: false,
    });
  };

  // Input Change Handler
  const handleFamilyInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'firstName' || name === 'lastName') {
      const alphaOnly = value.replace(/[^a-zA-Z\s]/g, '');
      setFamilyFormData((prev) => ({
        ...prev,
        [name]: alphaOnly,
      }));
      return;
    }
    
    setFamilyFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validate Form
  const validateFamilyForm = () => {
    const errors = {
      firstName: !familyFormData.firstName || familyFormData.firstName.trim().length === 0 || familyFormData.firstName.length > 100,
      lastName: !familyFormData.lastName || familyFormData.lastName.trim().length === 0 || familyFormData.lastName.length > 100,
      dateOfBirth: false,
      gender: false,
      relationship: false,
      gotram: familyFormData.gotram && familyFormData.gotram.trim().length > 0 && familyFormData.gotram.length > 50,
      nastram: familyFormData.nastram && familyFormData.nastram.trim().length > 0 && familyFormData.nastram.length > 50,
    };

    // DOB, Gender, and Relationship are now optional (hidden fields)
    // Only validate DOB format if provided
    if (familyFormData.dateOfBirth && familyFormData.dateOfBirth.trim() !== '') {
      const birthDate = new Date(familyFormData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      
      let exactAge = age;
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        exactAge--;
      }
      
      if (exactAge < 3 || exactAge > 99 || birthDate > today || birthDate.getFullYear() < 1900) {
        errors.dateOfBirth = true;
      }
    }

    setFamilyFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  // Save Student Handler with API call
  const handleSaveFamilyMember = async () => {
    if (!validateFamilyForm()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please complete all required fields correctly',
        icon: 'warning',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    const userId = user?.userId || user?.user_Id || user?.data?.userId || user?.data?.user_Id || user?.userData?.user_Id;

    if (!userId || userId <= 0) {
      Swal.fire({
        title: 'Error',
        text: 'User ID is missing. Please log in again.',
        icon: 'error',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    const studentData = {
      userId: userId,
      firstName: familyFormData.firstName.trim(),
      lastName: familyFormData.lastName.trim(),
      dateOfBirth: familyFormData.dateOfBirth || null,
      gender: familyFormData.gender ? (familyFormData.gender === 'Male' ? 'M' : 'F') : null,
      relationship: familyFormData.relationship || null,
      gotram: familyFormData.gotram.trim(),
      nastram: familyFormData.nastram.trim(),
    };

    try {
      if (selectedFamilyMember) {
        studentData.studentId = selectedFamilyMember.id;
        await eventService.updateStudent(studentData);
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Student saved successfully!',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        });
      } else {
        await eventService.addStudent(studentData);
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Student added successfully!',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        });
      }

      handleCloseFamilyModal();
      hasFetchedRef.current = false;
      const studentsData = await eventService.getStudents(userId);
      const students = studentsData?.students || studentsData || [];
      const formattedMembers = students.map(student => ({
        id: student.studentId || student.student_Id || student.Student_Id,
        firstName: student.firstName || student.student_First_Name || student.Student_First_Name || '',
        lastName: student.lastName || student.student_Last_Name || student.Student_Last_Name || '',
        dateOfBirth: student.dateOfBirth || student.student_Date_Of_Birth || student.Student_Date_Of_Birth || '',
        gender: student.gender || student.student_Gender || student.Student_Gender || '',
        relationship: student.relationship || student.student_Relationship || student.Student_Relationship || '',
        gotram: student.gotram || student.student_Gothram || student.Student_Gothram || '',
        nastram: student.nastram || student.student_Nakshatram || student.Student_Nakshatram || '',
      }));

      // Filter out the devotee from family members (anyone with relationship='Self')
      const filteredMembers = formattedMembers.filter(member => {
        const relationship = (member.relationship || '').toLowerCase().trim();
        if (relationship === 'self') {
          console.log('FamilySelection - Filtering out devotee with relationship=Self:', member.firstName, member.lastName);
          return false;
        }
        return true;
      });

      dispatch(setFamilyMembers(filteredMembers));
    } catch (error) {
      console.error('Error saving student:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to save student. Please try again.',
        icon: 'error',
        confirmButtonColor: '#3b82f6'
      });
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-50/70 via-white to-rose-50/70 pb-24">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-purple-200/35 blur-3xl"></div>
        <div className="absolute top-10 -right-24 h-72 w-72 rounded-full bg-blue-200/35 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-rose-200/25 blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-[28px] bg-white/75 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4">
            <RegistrationStepperHeader
              title={selectedEvent?.event_Name || 'Event Registration'}
              subtitle={`${selectedEvent?.event_Date ? new Date(selectedEvent.event_Date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}${selectedEvent?.event_Time ? ` • ${selectedEvent.event_Time}` : ''}${selectedCenter?.center_Name ? ` • ${selectedCenter.center_Name}` : ''}`}
              backPath="/devotee"
              backLabel="Back"
              eventDetails={eventDetails}
            />

            <div className="p-6 md:p-8">
              <div className="rounded-2xl bg-white/90 border border-gray-200 shadow-sm p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-1">Family Members</div>
                    <div className="text-xs text-gray-600">Add members like selection cards.</div>
                  </div>

                  <button
                    onClick={handleAddStudent}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 px-4 py-2 text-xs font-semibold text-white shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <i className="fas fa-plus"></i>
                    Add Family Member
                  </button>
                </div>

                <div className="mt-5">
                  {loading ? (
                    <div className="text-center py-10">
                      <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading family members...</p>
                    </div>
                  ) : familyMembers.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {familyMembers.map((member) => {
                          const isRegistered = registeredMemberIds.includes(member.id);
                          return (
                            <div
                              key={member.id}
                              onClick={() => {
                                if (!isRegistered) {
                                  console.log('Card clicked for member:', member.id);
                                  handleToggleMember(member.id);
                                }
                              }}
                              className={`item-card relative p-0 overflow-hidden rounded-[22px] transition-all duration-300 border ${
                                isRegistered
                                  ? 'opacity-60 cursor-not-allowed bg-gray-100 border-gray-300'
                                  : isMemberSelected(member.id)
                                  ? 'cursor-pointer selected ring-2 ring-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200'
                                  : 'cursor-pointer bg-white/90 hover:bg-white border-gray-200 hover:shadow-xl'
                              }`}
                            >
                              {isRegistered && (
                                <div className="absolute top-3 left-3 z-20">
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-500 text-white rounded-full text-[10px] font-bold shadow-md">
                                    <i className="fas fa-check-circle"></i>
                                    Registered
                                  </span>
                                </div>
                              )}
                              
                              <div className="p-5">
                                {!isRegistered && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditStudent(e, member);
                                    }}
                                    className="absolute top-4 right-4 w-9 h-9 bg-white/90 hover:bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-md z-10 border border-gray-200"
                                    title="Edit Student"
                                  >
                                    <i className="fas fa-edit text-xs"></i>
                                  </button>
                                )}

                                <div className="text-center">
                                  <div className={`mx-auto mb-3 h-14 w-14 rounded-full flex items-center justify-center text-2xl text-white shadow-md ${
                                    isRegistered 
                                      ? 'bg-gray-400' 
                                      : 'bg-gradient-to-br from-indigo-600 via-purple-600 to-amber-500'
                                  }`}>
                                    {member.gender?.toLowerCase() === 'male' || member.gender?.toLowerCase() === 'm' ? '👦' : '👧'}
                                  </div>
                                  <h3 className={`text-sm font-semibold mb-1 truncate ${
                                    isRegistered ? 'text-gray-500' : 'text-gray-900'
                                  }`}>
                                    {member.firstName} {member.lastName}
                                  </h3>
                                  <div className="text-xs text-gray-600 space-y-1">
                                    {member.relationship && (
                                      <p className="truncate">{member.relationship}</p>
                                    )}
                                    {member.nastram && (
                                      <p className="truncate">{member.nastram}</p>
                                    )}
                                  </div>
                                  <div className="mt-3 text-[11px] font-semibold text-gray-500">
                                    {isRegistered 
                                      ? 'Already registered for this event' 
                                      : isMemberSelected(member.id) 
                                      ? 'Selected' 
                                      : 'Tap to select'}
                                  </div>
                                  {!isRegistered && (
                                    <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${
                                          isMemberSelected(member.id)
                                            ? 'w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500'
                                            : 'w-0 bg-gray-200'
                                        }`}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {selectedFamilyMemberIds.length > 0 && (
                        <div className="mt-6 rounded-2xl bg-white/80 border border-gray-200 p-4 shadow-sm">
                          <h3 className="text-sm font-semibold text-gray-900 mb-2">
                            Selected Members ({selectedFamilyMemberIds.length})
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {familyMembers
                              .filter(m => selectedFamilyMemberIds.includes(m.id))
                              .map((member) => (
                                <span key={member.id} className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-800 rounded-full text-xs font-semibold border border-indigo-100">
                                  {member.firstName} {member.lastName}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12 bg-white/70 rounded-2xl border border-gray-200 shadow-sm">
                      <i className="fas fa-users text-gray-300 text-5xl mb-4"></i>
                      <p className="text-gray-700 text-sm font-semibold mb-1">No family members found</p>
                      <p className="text-gray-500 text-xs">You can continue without selecting family members</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StepperButtons
        backPath="/devotee"
        nextPath="/overview"
        nextLabel="Continue"
        loading={loading}
        containerClassName="px-4 py-3"
        wrapperClassName="bg-white/90 backdrop-blur-xl"
        centerContent={
          <div className="text-right">
            <div className="text-[11px] text-gray-500">Total:</div>
            <div className="text-sm font-semibold text-gray-900">${(totalPrice || 0).toFixed(2)}</div>
          </div>
        }
      />

      {/* Add/Edit Student Modal */}
      {showAddFamilyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                  {selectedFamilyMember ? 'Edit Family Member' : 'Add Family Member'}
                </h2>
                <button
                  onClick={handleCloseFamilyModal}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={familyFormData.firstName}
                    onChange={handleFamilyInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      familyFormErrors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                  />
                  {familyFormErrors.firstName && (
                    <p className="text-red-500 text-xs mt-1">First name is required (max 100 characters)</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={familyFormData.lastName}
                    onChange={handleFamilyInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      familyFormErrors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                  />
                  {familyFormErrors.lastName && (
                    <p className="text-red-500 text-xs mt-1">Last name is required (max 100 characters)</p>
                  )}
                </div>

                {/* Hidden fields - DOB, Gender, Relationship kept in state but not displayed */}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Gotram
                  </label>
                  <input
                    type="text"
                    name="gotram"
                    value={familyFormData.gotram}
                    onChange={handleFamilyInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      familyFormErrors.gotram ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter gotram"
                  />
                  {familyFormErrors.gotram && (
                    <p className="text-red-500 text-xs mt-1">Gotram cannot exceed 50 characters</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nakshatram
                  </label>
                  <input
                    type="text"
                    name="nastram"
                    value={familyFormData.nastram}
                    onChange={handleFamilyInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      familyFormErrors.nastram ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter nakshatram"
                  />
                  {familyFormErrors.nastram && (
                    <p className="text-red-500 text-xs mt-1">Nakshatram cannot exceed 50 characters</p>
                  )}
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={handleCloseFamilyModal}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFamilyMember}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {selectedFamilyMember ? 'Update' : 'Add'} Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilySelection;
